/**
 * Slip Scanner page — iter 169.
 *
 * Two surfaces:
 *   • All-roles ("Capture" tab): snap a slip from the phone camera,
 *     pick a slip kind, optional note → goes to a background OCR queue.
 *     Upload uses the resume-safe singleton manager so a flaky network
 *     never loses the slip.
 *   • Owner / Admin / Manager ("Review queue" tab): list of pending
 *     slips with extracted vendor/amount/date + thumbnail → assign to
 *     project/category/person and mark reviewed.
 *
 * No modals — inline expand for assignment. Trust contract honoured.
 */
import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  Camera, FileText, Loader2, ArrowLeft, CheckCircle2,
  ChevronRight, ScanText, X, AlertCircle, Tags, Building2,
} from "lucide-react";
import api from "../lib/api";
import { toast } from "../components/ui/sonner";
import { enqueue as enqueueUpload } from "../lib/uploadManager";
import SearchableSelect from "../components/SearchableSelect";
import { fmtDateTime } from "../lib/datetime";
import DictTextarea from "../components/DictTextarea";

const SLIP_KINDS = [
  { key: "expense", label: "Expense slip" },
  { key: "delivery", label: "Delivery slip" },
  { key: "fuel", label: "Fuel slip" },
  { key: "supplier", label: "Supplier slip" },
  { key: "other", label: "Other" },
];

export default function SlipScannerPage() {
  const [params, setParams] = useSearchParams();
  const focusId = params.get("focus");

  const [me, setMe] = useState(null);
  const [tab, setTab] = useState("capture");
  const [kind, setKind] = useState("expense");
  const [note, setNote] = useState("");
  const [queue, setQueue] = useState([]);
  const [loadingQueue, setLoadingQueue] = useState(false);
  const [expanded, setExpanded] = useState(focusId || null);
  const [uploading, setUploading] = useState(0); // count in-flight
  const [recentMine, setRecentMine] = useState([]);

  useEffect(() => {
    api.get("/auth/me").then((r) => setMe(r.data)).catch(() => setMe({}));
  }, []);

  const isStaff = me && ["owner", "admin", "manager"].includes(me.role);

  useEffect(() => {
    if (!me) return;
    if (isStaff) loadQueue();
    loadMine();
  }, [me, isStaff]);

  // Auto-focus a specific slip if ?focus=… is set (deep-link from alerts).
  useEffect(() => {
    if (focusId) {
      setTab("queue");
      setExpanded(focusId);
    }
  }, [focusId]);

  const loadQueue = async () => {
    setLoadingQueue(true);
    try {
      const r = await api.get("/slips", { params: { status: "pending_review" } });
      const pending = await api.get("/slips", { params: { status: "pending_ocr" } });
      const merged = [...(pending.data?.items || []), ...(r.data?.items || [])];
      setQueue(merged);
    } catch {
      /* may be 403 for techs — fine */
    }
    setLoadingQueue(false);
  };

  const loadMine = async () => {
    try {
      const r = await api.get("/slips/mine");
      setRecentMine(r.data?.items || []);
    } catch {
      /* ignore */
    }
  };

  const onSnap = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading((u) => u + files.length);
    let ok = 0;
    for (const f of files) {
      try {
        // Use the resume-safe queue with slip_scan attach kind.
        enqueueUpload({
          file: f,
          target_kind: "slip_scan",
          target_id: `slip:${Date.now()}`,
          label: `Slip: ${kind}`,
          attach_kind: "slip_scan",
          attach_payload: { note, kind },
          attach: async (_rec) => {
            setUploading((u) => Math.max(0, u - 1));
            ok += 1;
            // Refresh mine list shortly after attach lands.
            setTimeout(() => { loadMine(); if (isStaff) loadQueue(); }, 600);
          },
        });
      } catch (err) {
        setUploading((u) => Math.max(0, u - 1));
        toast.error(err?.message || "Could not queue slip");
      }
    }
    e.target.value = "";
    toast.success(`${files.length} slip${files.length === 1 ? "" : "s"} queued for OCR`);
  };

  return (
    <div className="space-y-4" data-testid="slip-scanner-page">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <Link to="/" className="hv-btn hv-btn-ghost text-xs" data-testid="slip-back">
          <ArrowLeft className="w-3.5 h-3.5" /> Back
        </Link>
        <h1 className="font-display text-xl sm:text-2xl font-bold inline-flex items-center gap-2">
          <ScanText className="w-5 h-5 text-[#1FB6C1]" /> Slip scanner
        </h1>
        <div className="w-12" />
      </div>

      {isStaff && (
        <div className="flex items-center gap-2 border-b border-[#22344a]">
          <button
            type="button"
            onClick={() => { setTab("capture"); setParams({}); }}
            className={`px-3 py-2 text-xs font-medium border-b-2 -mb-px ${tab === "capture" ? "border-[#1FB6C1] text-white" : "border-transparent text-[#9aa3b5]"}`}
            data-testid="slip-tab-capture"
          >
            Capture
          </button>
          <button
            type="button"
            onClick={() => { setTab("queue"); loadQueue(); }}
            className={`px-3 py-2 text-xs font-medium border-b-2 -mb-px inline-flex items-center gap-1 ${tab === "queue" ? "border-[#1FB6C1] text-white" : "border-transparent text-[#9aa3b5]"}`}
            data-testid="slip-tab-queue"
          >
            Review queue
            {queue.length > 0 && (
              <span className="bg-[#F59E0B] text-black text-[10px] font-bold rounded-full px-1.5 py-0.5">
                {queue.length}
              </span>
            )}
          </button>
        </div>
      )}

      {tab === "capture" && (
        <CapturePanel
          kind={kind} setKind={setKind}
          note={note} setNote={setNote}
          uploading={uploading}
          recentMine={recentMine}
          onSnap={onSnap}
        />
      )}

      {tab === "queue" && isStaff && (
        <ReviewQueuePanel
          rows={queue}
          loading={loadingQueue}
          expanded={expanded}
          setExpanded={setExpanded}
          onChange={loadQueue}
        />
      )}
    </div>
  );
}

// ── Capture (all roles) ─────────────────────────────────────────
function CapturePanel({ kind, setKind, note, setNote, uploading, recentMine, onSnap }) {
  return (
    <div className="space-y-4">
      <div className="hv-card p-4 space-y-3" data-testid="slip-capture-form">
        <div className="text-xs uppercase tracking-[0.2em] text-[#9aa3b5]">Slip kind</div>
        <div className="flex flex-wrap gap-2">
          {SLIP_KINDS.map((k) => (
            <button
              key={k.key}
              type="button"
              onClick={() => setKind(k.key)}
              className={`px-3 py-1.5 text-xs rounded-full border transition-colors ${
                kind === k.key
                  ? "bg-[#1FB6C1] text-black border-[#1FB6C1]"
                  : "border-[#22344a] text-[#bcc8de] hover:border-[#1FB6C1]"
              }`}
              data-testid={`slip-kind-${k.key}`}
            >
              {k.label}
            </button>
          ))}
        </div>
        <DictTextarea
          rows={2}
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="Optional note (job number, customer, supplier…)"
          className="hv-input w-full text-sm min-h-[60px]"
          data-testid="slip-note"
        />
        <label
          className="hv-btn hv-btn-primary w-full sm:w-auto inline-flex items-center justify-center gap-2 cursor-pointer text-sm"
          data-testid="slip-capture-button"
        >
          <Camera className="w-4 h-4" />
          {uploading > 0 ? `Queuing… (${uploading})` : "Snap or upload"}
          <input
            type="file"
            accept="image/*,application/pdf"
            multiple
            capture="environment"
            onChange={onSnap}
            className="hidden"
            data-testid="slip-file-input"
          />
        </label>
        <div className="text-[11px] text-[#9aa3b5] flex items-center gap-1.5">
          <CheckCircle2 className="w-3 h-3 text-[#22C55E]" />
          Uploads are resume-safe — even if you lose signal or close the app, your slips will be sent when you're back online.
        </div>
      </div>

      {recentMine.length > 0 && (
        <div className="hv-card p-4">
          <div className="text-xs uppercase tracking-[0.2em] text-[#9aa3b5] mb-2">My recent slips</div>
          <div className="space-y-1">
            {recentMine.slice(0, 10).map((s) => (
              <div
                key={s.id}
                className="flex items-center justify-between gap-2 text-xs py-1.5 border-b border-[#22344a]/40 last:border-0"
                data-testid={`slip-mine-${s.id}`}
              >
                <span className="font-medium capitalize">{s.kind}</span>
                <span className="text-[#9aa3b5] flex-1 mx-2 truncate">{s.note || s.vendor || "—"}</span>
                <span className="text-[10px] px-2 py-0.5 rounded-full border border-[#22344a] text-[#bcc8de] capitalize">
                  {(s.status || "").replace(/_/g, " ")}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Review queue (staff) ────────────────────────────────────────
function ReviewQueuePanel({ rows, loading, expanded, setExpanded, onChange }) {
  if (loading) {
    return (
      <div className="hv-card p-8 text-center text-[#9aa3b5] inline-flex items-center justify-center gap-2 w-full">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading queue…
      </div>
    );
  }
  if (!rows.length) {
    return (
      <div className="hv-card p-8 text-center text-[#9aa3b5]" data-testid="slip-queue-empty">
        <CheckCircle2 className="w-6 h-6 mx-auto mb-2 text-[#22C55E]" />
        Inbox zero. No slips waiting for review.
      </div>
    );
  }
  return (
    <div className="space-y-2">
      {rows.map((row) => (
        <SlipRow
          key={row.id}
          row={row}
          expanded={expanded === row.id}
          onExpand={() => setExpanded(expanded === row.id ? null : row.id)}
          onChange={onChange}
        />
      ))}
    </div>
  );
}

function SlipRow({ row, expanded, onExpand, onChange }) {
  const [busy, setBusy] = useState(false);
  const [projects, setProjects] = useState([]);
  const [users, setUsers] = useState([]);
  const [form, setForm] = useState({
    assignee_id: row.assignee_id || "",
    project_id: row.project_id || "",
    category: row.category || "",
    amount: row.amount || "",
    vendor: row.vendor || "",
    slip_date: row.slip_date || "",
    note: "",
  });

  useEffect(() => {
    if (!expanded) return;
    if (projects.length === 0) {
      api.get("/projects").then((r) => setProjects(r.data?.items || r.data || [])).catch(() => {});
    }
    if (users.length === 0) {
      api.get("/users").then((r) => setUsers(r.data?.items || r.data || [])).catch(() => {});
    }
  }, [expanded]);

  const onAssign = async () => {
    setBusy(true);
    try {
      const payload = {};
      for (const k of ["assignee_id", "project_id", "category", "vendor", "slip_date", "note"]) {
        if (form[k]) payload[k] = form[k];
      }
      if (form.amount !== "" && form.amount != null) payload.amount = Number(form.amount);
      await api.post(`/slips/${row.id}/assign`, payload);
      toast.success("Slip updated");
      onChange?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update slip");
    }
    setBusy(false);
  };

  const onReviewed = async () => {
    setBusy(true);
    try {
      await api.post(`/slips/${row.id}/mark-reviewed`, { final_note: form.note || "" });
      toast.success("Marked reviewed");
      onChange?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not mark reviewed");
    }
    setBusy(false);
  };

  const onDismiss = async () => {
    if (!window.confirm("Dismiss this slip? It won't appear in the queue anymore.")) return;
    setBusy(true);
    try {
      await api.delete(`/slips/${row.id}`);
      toast.success("Slip dismissed");
      onChange?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not dismiss");
    }
    setBusy(false);
  };

  const isOcr = row.status === "pending_ocr";

  return (
    <div className="hv-card overflow-hidden" data-testid={`slip-row-${row.id}`}>
      <button
        type="button"
        className="w-full text-left p-3 flex items-center gap-3 hover:bg-white/5"
        onClick={onExpand}
        data-testid={`slip-row-${row.id}-expand`}
      >
        <div className="w-12 h-12 rounded bg-[#0d1622] border border-[#22344a] overflow-hidden flex items-center justify-center flex-shrink-0">
          {row.upload_id ? (
            <img
              src={`/api/uploads/${row.upload_id}/thumb`}
              alt=""
              className="w-full h-full object-cover"
              onError={(e) => { e.target.style.display = "none"; }}
            />
          ) : (
            <FileText className="w-5 h-5 text-[#647087]" />
          )}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-medium capitalize text-sm">{row.kind || "slip"}</span>
            {isOcr ? (
              <span className="text-[10px] px-2 py-0.5 rounded-full bg-[#F59E0B]/20 text-[#fde2a7] inline-flex items-center gap-1">
                <Loader2 className="w-3 h-3 animate-spin" /> Scanning…
              </span>
            ) : (
              <span className="text-[10px] px-2 py-0.5 rounded-full bg-[#1FB6C1]/20 text-[#bff3f7]">
                Ready for review
              </span>
            )}
            {row.vendor && (
              <span className="text-[11px] text-[#bcc8de] inline-flex items-center gap-1">
                <Building2 className="w-3 h-3" /> {row.vendor}
              </span>
            )}
            {row.amount != null && (
              <span className="text-[11px] text-[#bcc8de] font-mono">R {Number(row.amount).toFixed(2)}</span>
            )}
          </div>
          <div className="text-[11px] text-[#9aa3b5] mt-0.5 truncate">
            {row.uploaded_by_name || "Unknown"} · {fmtDateTime(row.created_at)}
            {row.note ? ` · ${row.note}` : ""}
          </div>
        </div>
        <ChevronRight className={`w-4 h-4 text-[#647087] transition-transform ${expanded ? "rotate-90" : ""}`} />
      </button>

      {expanded && (
        <div className="border-t border-[#22344a] p-3 space-y-3" data-testid={`slip-row-${row.id}-detail`}>
          {row.upload_id && (
            <a
              href={`/api/uploads/${row.upload_id}`}
              target="_blank"
              rel="noreferrer"
              className="block max-h-96 overflow-hidden rounded border border-[#22344a]"
            >
              <img
                src={`/api/uploads/${row.upload_id}/display`}
                alt="Slip"
                className="w-full object-contain"
                onError={(e) => {
                  e.target.src = `/api/uploads/${row.upload_id}`;
                }}
              />
            </a>
          )}
          {row.extracted_text && (
            <div className="text-[11px] text-[#bcc8de] bg-[#0d1622] border border-[#22344a] rounded p-2 max-h-40 overflow-y-auto whitespace-pre-wrap">
              {row.extracted_text}
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <label className="text-xs space-y-1">
              <span className="text-[#9aa3b5] inline-flex items-center gap-1"><Building2 className="w-3 h-3" /> Vendor</span>
              <input
                className="hv-input w-full text-sm"
                value={form.vendor}
                onChange={(e) => setForm((f) => ({ ...f, vendor: e.target.value }))}
                data-testid={`slip-${row.id}-vendor`}
              />
            </label>
            <label className="text-xs space-y-1">
              <span className="text-[#9aa3b5]">Amount (R)</span>
              <input
                type="number"
                step="0.01"
                className="hv-input w-full text-sm"
                value={form.amount}
                onChange={(e) => setForm((f) => ({ ...f, amount: e.target.value }))}
                data-testid={`slip-${row.id}-amount`}
              />
            </label>
            <label className="text-xs space-y-1">
              <span className="text-[#9aa3b5]">Slip date (dd/mm/yyyy)</span>
              <input
                type="text"
                placeholder="dd/mm/yyyy"
                className="hv-input w-full text-sm"
                value={form.slip_date}
                onChange={(e) => setForm((f) => ({ ...f, slip_date: e.target.value }))}
                data-testid={`slip-${row.id}-date`}
              />
            </label>
            <label className="text-xs space-y-1">
              <span className="text-[#9aa3b5] inline-flex items-center gap-1"><Tags className="w-3 h-3" /> Category</span>
              <input
                type="text"
                placeholder="e.g. fuel, materials, courier"
                className="hv-input w-full text-sm"
                value={form.category}
                onChange={(e) => setForm((f) => ({ ...f, category: e.target.value }))}
                data-testid={`slip-${row.id}-category`}
              />
            </label>
            <label className="text-xs space-y-1">
              <span className="text-[#9aa3b5]">Assign to project</span>
              <SearchableSelect
                value={form.project_id || ""}
                onChange={(v) => setForm((f) => ({ ...f, project_id: v }))}
                options={projects.map((p) => ({
                  id: p.id,
                  label: `${p.short_id ? `${p.short_id} · ` : ""}${p.title || p.name}`,
                  sublabel: p.customer_name || p.status || "",
                  searchTerms: [p.short_id, p.title, p.name, p.customer_name].filter(Boolean),
                }))}
                placeholder="— none —"
                testId={`slip-${row.id}-project`}
              />
            </label>
            <label className="text-xs space-y-1">
              <span className="text-[#9aa3b5]">Assign to person</span>
              <SearchableSelect
                value={form.assignee_id || ""}
                onChange={(v) => setForm((f) => ({ ...f, assignee_id: v }))}
                options={users.map((u) => ({
                  id: u.id,
                  label: u.name || u.email || u.id,
                  sublabel: u.name && u.email ? u.email : "",
                  searchTerms: [u.name, u.email].filter(Boolean),
                }))}
                placeholder="— unassigned —"
                testId={`slip-${row.id}-assignee`}
              />
            </label>
          </div>

          <DictTextarea
            rows={2}
            placeholder="Final note (optional)"
            className="hv-input w-full text-sm"
            value={form.note}
            onChange={(e) => setForm((f) => ({ ...f, note: e.target.value }))}
            data-testid={`slip-${row.id}-note`}
          />

          <div className="flex flex-wrap gap-2 items-center justify-end">
            <button
              type="button"
              onClick={onDismiss}
              disabled={busy}
              className="hv-btn hv-btn-ghost text-xs inline-flex items-center gap-1 text-[#F43F5E]"
              data-testid={`slip-${row.id}-dismiss`}
            >
              <X className="w-3 h-3" /> Dismiss
            </button>
            <button
              type="button"
              onClick={onAssign}
              disabled={busy}
              className="hv-btn hv-btn-ghost text-xs"
              data-testid={`slip-${row.id}-save`}
            >
              {busy ? <Loader2 className="w-3 h-3 animate-spin inline" /> : "Save"}
            </button>
            <button
              type="button"
              onClick={onReviewed}
              disabled={busy || row.status === "pending_ocr"}
              className="hv-btn hv-btn-primary text-xs inline-flex items-center gap-1"
              data-testid={`slip-${row.id}-reviewed`}
            >
              <CheckCircle2 className="w-3 h-3" /> Mark reviewed
            </button>
          </div>
          {row.status === "pending_ocr" && (
            <div className="text-[11px] text-[#F59E0B] inline-flex items-center gap-1">
              <AlertCircle className="w-3 h-3" /> OCR still running — fields will populate shortly.
            </div>
          )}
        </div>
      )}
    </div>
  );
}
